/**
 * Tally form (S04) — optional migration intent.
 *
 * The user can either upload a Tally backup (file picker) or skip. Both paths
 * call `onboarding.recordMigrationIntent`; on success we navigate to
 * `/onboarding/done`. Upload is captured-as-intent only in V1.0.x — actual
 * parsing is the migration vertical's job (V1.x).
 *
 * Inline pieces (dropzone, intent banner, company chip) live in this file
 * because they're only used here. If a second screen needs them, lift to
 * `features/onboarding/components/<name>.tsx`.
 *
 * @see prototype/onboarding/tally.html — S04 baseline
 */
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "@tanstack/react-router";
import { Info, Plus, UploadCloud } from "lucide-react";
import { Card, CardBody, Form, Submit, SubmitProbe, MetaPill, cn } from "@flint/ui";
import { onboarding } from "@flint/api-contract";
import type { z } from "zod";
import { trpc } from "../../../lib/trpc.ts";

type FormValues = z.infer<typeof onboarding.recordMigrationIntentInput>;

export function TallyForm() {
  const navigate = useNavigate();
  const [intentMessage, setIntentMessage] = useState<{
    title: string;
    body: string;
  } | null>(null);

  const form = useForm<FormValues>({
    defaultValues: { source: "tally", deferred: true },
  });

  const recordIntent = trpc.onboarding.recordMigrationIntent.useMutation();

  const onFile = async (file: File) => {
    const content = await file.text();
    setIntentMessage({
      title: "Tally XML ready to import",
      body: `Selected "${file.name}". Flint will import party ledgers and stock items when you continue.`,
    });
    form.setValue("fileName", file.name);
    form.setValue("fileContent", content);
    form.setValue("deferred", false);
  };

  const onSubmit = form.handleSubmit(async (values) => {
    await recordIntent.mutateAsync(values);
    void navigate({ to: "/onboarding/done" });
  });

  return (
    <Form {...form}>
      <SubmitProbe>
        <form onSubmit={onSubmit} noValidate>
          <Card>
            <CardBody className="flex flex-col gap-3.5">
              <span className="self-start inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-raised text-ink-2 text-xs font-medium">
                <span aria-hidden className="w-1 h-1 rounded-full bg-sky-600" />
                Optional migration
              </span>

              <div className="flex flex-col gap-1">
                <h2 className="m-0 text-base leading-[22px] font-semibold tracking-[-0.01em] text-ink">
                  Tally XML import
                </h2>
                <p className="m-0 text-sm leading-[18px] text-muted">
                  Upload a Tally XML export to import party ledgers and stock items now. If you
                  do not have the file handy, continue without it and Flint can follow up.
                </p>
              </div>

              <Dropzone onFile={onFile} />

              {intentMessage ? (
                <div className="flex items-start gap-2.5 p-3 rounded bg-info-50 ring-1 ring-info-100/60">
                  <span
                    aria-hidden
                    className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-info-100 text-info mt-px"
                  >
                    <Info size={11} />
                  </span>
                  <div className="flex flex-col gap-0.5 min-w-0">
                    <strong className="text-xs leading-[16px] font-semibold text-ink">
                      {intentMessage.title}
                    </strong>
                    <p className="m-0 text-xs leading-[17px] text-ink-2">
                      {intentMessage.body}
                    </p>
                  </div>
                </div>
              ) : null}

              <Submit pendingLabel="Continuing" disabled={recordIntent.isPending}>
                Continue into workspace
              </Submit>

              <p className="text-xs leading-[16px] text-muted">
                XML import covers customers, vendors, items, and customer opening balances.
                Transaction vouchers and binary Tally backups are still handled as follow-up work.
              </p>

              {recordIntent.error ? (
                <p role="alert" className="text-xs text-bad">
                  {recordIntent.error.message}
                </p>
              ) : null}
            </CardBody>
          </Card>

          <div className="flex flex-wrap gap-1.5 mt-3">
            <MetaPill>White-glove available</MetaPill>
            <MetaPill>Intent captured</MetaPill>
          </div>
        </form>
      </SubmitProbe>
    </Form>
  );
}

function Dropzone({ onFile }: { onFile: (file: File) => void | Promise<void> }) {
  const [hover, setHover] = useState(false);
  return (
    <label
      className={cn(
        "relative block p-4 rounded cursor-pointer transition-colors text-left",
        "bg-paper ring-1 ring-line/80",
        hover && "bg-bg ring-sky-100/80",
        "focus-within:ring-2 focus-within:ring-sky-100",
      )}
      onDragOver={(e) => {
        e.preventDefault();
        setHover(true);
      }}
      onDragLeave={() => setHover(false)}
      onDrop={(e) => {
        e.preventDefault();
        setHover(false);
        const file = e.dataTransfer.files?.[0];
        if (file) void onFile(file);
      }}
    >
      <div className="flex items-center gap-2.5 mb-2.5">
        <span
          aria-hidden
          className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded bg-raised text-ink-2"
        >
          <UploadCloud size={18} />
        </span>
        <div className="flex flex-col gap-0.5 min-w-0">
          <strong className="text-sm leading-[18px] font-semibold text-ink">
            Select a Tally XML export
          </strong>
          <span className="text-xs leading-[15px] text-muted">
            Flint reads the XML and imports supported masters into this workspace.
          </span>
        </div>
      </div>

      <p className="m-0 text-xs leading-[17px] text-muted">
        Use a Tally XML export with LEDGER and STOCKITEM masters. Voucher migration and
        confidence review will land as a later migration capability.
      </p>

      <div className="flex justify-between items-center gap-3 flex-wrap mt-3">
        <span className="inline-flex items-center gap-2 h-[34px] px-3.5 rounded-sm bg-ink text-paper text-sm font-medium">
          <Plus size={14} />
          <span>Select file</span>
        </span>
        <div className="flex items-center gap-1.5 flex-wrap">
          {[".xml"].map((ext) => (
            <span
              key={ext}
              className="inline-flex items-center h-[22px] px-2 rounded-full bg-raised text-ink-2 text-xs uppercase tracking-[0.04em] font-medium"
            >
              {ext}
            </span>
          ))}
        </div>
      </div>

      <input
        type="file"
        accept=".xml"
        hidden
        onChange={(e) => {
          const file = e.currentTarget.files?.[0];
          if (file) void onFile(file);
          e.currentTarget.value = "";
        }}
      />
    </label>
  );
}
